import { Link, NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/AuthContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Footer } from './Footer';
import { IconGuias, IconObras, IconPerfil } from './icons';

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((p) => p[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

const navItems = [
  { to: '/', end: true, key: 'nav.guias', Icon: IconGuias },
  { to: '/obras', end: false, key: 'nav.obras', Icon: IconObras },
  { to: '/perfil', end: false, key: 'nav.perfil', Icon: IconPerfil },
] as const;

export function AppLayout() {
  const { t } = useTranslation();
  const { user } = useAuth();

  return (
    <div className="min-h-dvh bg-conduril-surface">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-conduril-border bg-white md:flex">
        <div className="flex h-16 shrink-0 items-center border-b border-conduril-border px-5">
          <Link to="/" aria-label="Guias por Obra">
            <img src="/logo.svg" alt="Guias por Obra" className="h-8 w-auto" />
          </Link>
        </div>

        <nav className="flex-1 space-y-1 p-3">
          {navItems.map(({ to, end, key, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                  isActive
                    ? 'bg-conduril-tint text-conduril'
                    : 'text-conduril-muted hover:bg-conduril-surface hover:text-conduril-ink'
                }`
              }
            >
              <Icon className="h-5 w-5" />
              {t(key)}
            </NavLink>
          ))}
        </nav>

        {user && (
          <Link
            to="/perfil"
            className="flex items-center gap-3 border-t border-conduril-border p-3 hover:bg-conduril-surface"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-conduril text-xs font-bold text-white">
              {initialsOf(user.displayName)}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-conduril-ink">{user.displayName}</span>
              <span className="block truncate text-xs text-conduril-muted">{t(`roles.${user.role}`)}</span>
            </span>
          </Link>
        )}

        <div className="border-t border-conduril-border p-3">
          <LanguageSwitcher className="w-full" />
        </div>
      </aside>

      {/* Mobile top bar (navy fills the status-bar safe area) */}
      <header className="sticky top-0 z-30 bg-conduril pt-[env(safe-area-inset-top)] md:hidden">
        <div className="flex h-14 items-center justify-between gap-3 px-4">
          <Link to="/" aria-label="Guias por Obra">
            <img src="/logo-white.svg" alt="Guias por Obra" className="h-7 w-auto" />
          </Link>
          <LanguageSwitcher onDark />
        </div>
      </header>

      {/* Content */}
      <div className="md:pl-60">
        <main className="mx-auto w-full max-w-app px-4 pb-24 pt-4 md:px-8 md:pb-10 md:pt-8">
          <div className="animate-fade-in">
            <Outlet />
          </div>
          <Footer className="mt-10 border-t border-conduril-border" />
        </main>
      </div>

      {/* Mobile bottom nav (padded for the home-indicator safe area) */}
      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-3 border-t border-conduril-border bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        {navItems.map(({ to, end, key, Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold transition ${
                isActive ? 'text-conduril' : 'text-conduril-muted'
              }`
            }
          >
            <Icon className="h-6 w-6" />
            {t(key)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
